/// <amd-module name="TFS/Services/LocalSettingsService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { ITfsPageData, ITfsPageService, ITeamData } from "Tfs/Platform/Page";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

/**
 * Scope at which the local user setting applies
 */
export enum LocalSettingsScope {
    /**
     * Global (account-specific) settings for a user
     */
    Global,

    /**
     * Collection-specific settings for a user
     */
    Collection,

    /**
     * Project-specific settings for a user
     */
    Project,

    /**
     * Team-specific settings for a user
     */
    Team
}

export interface ILocalSettingsService extends IVssService {
    /**
     * Write a settings value to browser local storage
     *
     * @param key Key for the setting to be written. This key will be prefixed with a scope.
     * @param value Value for the setting to be written
     * @param scope Scope for the setting to apply to. This will determine the prefix to use at the beginning of the setting key.
     * @return True if the settings value was written successfully
     */
    write(key: string, value: any, scope: LocalSettingsScope): boolean;

    /**
     * Read a setting from browser local storage.
     *
     * @param key Key for the setting to be written. This key will be prefixed with a scope.
     * @param defaultValue The value to return in case no setting exists
     * @param scope Scope for the setting to apply to. This will determine the prefix to use at the beginning of the setting key.
     * @return Value read from the setting or undefined if no value stored
     */
    read<T>(key: string, defaultValue: T, scope: LocalSettingsScope): T;
    read<T>(key: string, defaultValue: T | undefined, scope: LocalSettingsScope): T | undefined;
}

/**
 * Service for reading and writing to local storage
 */
class LocalSettingsService extends VssService implements ILocalSettingsService {
    private static GLOBAL_SETTING_KEY = "global";
    private static COLLECTION_SETTING_KEY = "collection";
    private static PROJECT_SETTING_KEY = "project";
    private static TEAM_SETTING_KEY = "team";

    private tfsPageDataService: ITfsPageService;

    public _serviceStart(pageContext: IVssPageContext) {
        this.pageContext = pageContext;

        this.tfsPageDataService = pageContext.getService<ITfsPageService>("ITfsPageService");
    }

    public write(key: string, value: any, scope: LocalSettingsScope = LocalSettingsScope.Global): boolean {
        if (key.trim().length === 0) {
            return false;
        }

        const scopedKey = this._getScopedKey(key, scope);
        if (scopedKey) {
            try {
                window.localStorage.setItem(scopedKey, JSON.stringify({ v: value }));
                return true;
            } catch (error) {
                console.error("Failed to write to local storage: " + error);
                return false;
            }
        }
        return false;
    }

    public read<T>(key: string, defaultValue: T | undefined, scope: LocalSettingsScope = LocalSettingsScope.Global): T | undefined {
        if (key.trim().length === 0) {
            return undefined;
        }

        const scopedKey = this._getScopedKey(key, scope);
        if (scopedKey) {
            try {
                var value = window.localStorage.getItem(scopedKey);
                if (value && typeof value === "string") {
                    return <T>JSON.parse(value).v;
                }
            } catch (error) {
                console.warn("Failed to read from local storage: " + error);
            }
        }
        return defaultValue;
    }

    private _getScopedKey(key: string, scope: LocalSettingsScope): string | null {
        switch (scope) {
            case LocalSettingsScope.Global:
                return LocalSettingsService.GLOBAL_SETTING_KEY + "/" + key;

            case LocalSettingsScope.Collection:
                const collectionId = this.getCollectionId();
                if (!collectionId) {
                    console.warn("Collection scope requested for key '" + key + "' but no collection scope in context.");
                    return null;
                }
                return LocalSettingsService.COLLECTION_SETTING_KEY + "/" + collectionId + "/" + key;

            case LocalSettingsScope.Project:
                const projectId = this.getProjectId();
                if (!projectId) {
                    console.warn("Project scope requested for key '" + key + "' but no project scope in context.");
                    return null;
                }
                return LocalSettingsService.PROJECT_SETTING_KEY + "/" + projectId + "/" + key;

            case LocalSettingsScope.Team:
                const teamId = this.getTeamId();
                if (!teamId) {
                    console.warn("Team scope requested for key '" + key + "' but no team scope in context.");
                    return null;
                }
                return LocalSettingsService.TEAM_SETTING_KEY + "/" + teamId + "/" + key;

            default:
                console.error("Local storage: unknown scope");
                return null;
        }
    }

    private getCollectionId(): string | undefined {
        const pageData: IPageData | undefined = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
        return pageData && pageData.hostId;
    }

    private getProjectId(): string | undefined {
        const pageData: ITfsPageData | undefined = this.tfsPageDataService.getData();
        return pageData && pageData.project && pageData.project.id;
    }

    private getTeamId(): string | undefined {
        const teamData: ITeamData | undefined = this.tfsPageDataService.getHorizontalNavigationTeamData();
        return teamData && teamData.id;
    }
}

Services.add("ILocalSettingsService", { serviceFactory: LocalSettingsService });
